import { formatDateTime } from 'utils/dates/date.utils';

/**
 * Returns true if the alarm is silenced (silenced === 1).
 * @param alarm - Alarm object with optional silenced property
 * @returns {boolean} True if silenced, false otherwise
 */
export function isSilenced(alarm: { silenced?: number }): boolean {
  return alarm.silenced === 1;
}

/**
 * Returns a display label for alarm state.
 * @param state - Alarm state (number or string)
 * @returns {string} 'Active', 'Inactive', or the state as string
 */
export function getAlarmStateLabel(state: number | string): string {
  if (state === 1) return 'Active';
  if (state === 0) return 'Inactive';
  return String(state);
}

/**
 * Replaces 'global-actions' with 'Global Action' in a string.
 * @param str - Source string
 * @returns {string} Corrected string
 */
export function sourceStringCorrection(str: string): string {
  return str.replace('global-actions', 'Global Action');
}

// Returns a human-readable time since the given activity date

/**
 * Returns a human-readable string for time since the given activity date.
 * @param alarmActivity - Date string, number, or Date
 * @returns {string} Time since activity (e.g., '1 Days 02:03:04 hours') or 'Unknown'
 */
export function timeSinceActivity(alarmActivity: string | number | Date): string {
  let activityDate: Date;
  if (typeof alarmActivity === 'string') {
    // If the string does not contain a timezone, force UTC by appending 'Z'
    const hasTZ = /[zZ]|[+-]\d{2}:?\d{2}$/.test(alarmActivity);
    activityDate = new Date(hasTZ ? alarmActivity : alarmActivity + 'Z');
  } else {
    activityDate = new Date(alarmActivity);
  }
  if (Number.isNaN(activityDate.getTime())) {
    return 'Unknown';
  }

  const msSinceActivity = Date.now() - activityDate.getTime();
  const seconds = Math.floor(msSinceActivity / 1000) % 60;
  const minutes = Math.floor(msSinceActivity / 1000 / 60) % 60;
  const hours = Math.floor(msSinceActivity / 1000 / 60 / 60) % 24;
  const days = Math.floor(msSinceActivity / 1000 / 60 / 60 / 24);
  return `
    ${days > 0 ? `${days} Days` : ''}	
    ${hours >= 10 ? `${hours}` : `0${hours}`}:
    ${minutes >= 10 ? `${minutes}` : `0${minutes}`}:
    ${seconds >= 10 ? `${seconds}` : `0${seconds}`} hours
    `;
}

// Shorten a headline string to the first part before a semicolon, or 'Alarm' if empty

/**
 * Shortens a headline string to the first part before a semicolon, or 'Alarm' if empty.
 * @param headline - Headline string (nullable)
 * @returns {string} Shortened headline or 'Alarm'
 */
export function shortenHeadline(headline: string | null | undefined): string {
  const text = String(headline ?? '').trim();
  if (!text) return 'Alarm';
  return text.split(';')[0] || text;
}

/**
 * Returns true if a FEMA Cancel closed this alarm (backend card BE-27).
 * @param alarm - Alarm object with optional info.supersededReason
 * @returns {boolean} True if canceled, false otherwise
 */
export function isAlarmCanceled(alarm: { info?: { supersededReason?: string | null } }): boolean {
  return alarm?.info?.supersededReason === 'Cancel';
}

/**
 * Appends ' (Canceled)' to a headline when a FEMA Cancel closed the alarm.
 * @param headline - Headline string to display
 * @param alarm - Alarm object with optional info.supersededReason
 * @returns {string} Headline, with ' (Canceled)' when canceled
 */
export function withCanceledSuffix(
  headline: string,
  alarm: { info?: { supersededReason?: string | null } },
): string {
  return isAlarmCanceled(alarm) ? `${headline} (Canceled)` : headline;
}

// Get a display ID for an alarm object

/**
 * Returns a display ID for an alarm object based on available identifiers.
 * @param alarm - Alarm object
 * @returns {string} Display ID (e.g., 'GA_...', 'CA_...', 'CW_...', or 'ID_...')
 */
export function getAlarmIdentifier(alarm: any): string {
  if (alarm.ga_identifier) return `GA_${alarm.ga_identifier}`;
  if (alarm.ca_identifier) return `CA_${alarm.ca_identifier}`;
  if (alarm.cw_identifier) return `CW_${alarm.cw_identifier}`;
  return `ID_${alarm.id}`;
}

// Parse a key to a display label

/**
 * Maps a key to a display label for alarm info fields.
 * @param key - Info key
 * @returns {string} Display label or empty string
 */
export function parseKey(key: string): string {
  switch (key.toLowerCase()) {
    case 'type':
      return 'Alert Type';
    case 'nwsoffice':
      return 'Issuing Office';
    case 'starttime':
      return 'Start Time';
    case 'expiretime':
      return 'Expiration Time';
    case 'severity':
      return 'Severity';
    case 'timezone':
      return 'Time Zone';
    case 'latitude':
      return 'Latitude';
    case 'longitude':
      return 'Longitude';
    case 'location':
      return 'Location';
    case 'device_node_ids':
      return 'Related Device Id(s)';
    case 'group_name':
      return 'Related Group Name';
    // FEMA provenance (backend card BE-29): the CAP <identifier> the alarm was raised from, the CAP
    // <sender> address and the sender's display name. Present on every alarm, null outside FEMA.
    case 'femaidentifier':
      return 'FEMA Message Identifier';
    case 'sender':
      return 'Sender';
    case 'sendername':
      return 'Sender Name';
    // Written when an Update or a Cancel closed this alarm (backend card BE-27): the CAP identifier
    // of the message that replaced it, and whether that was an Update or a Cancel.
    case 'supersededby':
      return 'Superseded By';
    case 'supersededreason':
      return 'Superseded Reason';
    default:
      return '';
  }
}

// Returns a color for the severity slider background
export function alertColorScale(severity: number): string {
  if (severity >= 80) return '#d32f2f'; // Red
  if (severity >= 60) return '#f57c00'; // Orange
  if (severity >= 40) return '#fbc02d'; // Yellow
  if (severity >= 20) return '#388e3c'; // Green
  return '#1976d2'; // Blue
}

// Returns a label for the severity level
export function getSeverityLevel(severity: number): string {
  if (severity >= 80) return 'Extreme';
  if (severity >= 60) return 'Severe';
  if (severity >= 40) return 'Moderate';
  if (severity >= 20) return 'Minor';
  return 'Low';
}

// Utility to safely get a property from an object by key, with runtime and type safety.
export function safeGetProp<T extends object, K extends keyof T>(
  obj: T | undefined,
  key: K,
): T[K] | string | undefined {
  if (obj && Object.prototype.hasOwnProperty.call(obj, key)) {
    if (key === 'startTime' || key === 'expireTime') {
      return formatDateTime(obj[key], '2-digit');
    }
    if (key === 'device_node_ids' && Array.isArray(obj[key])) {
      return (obj[key] as unknown as string[]).join(', ');
    }
    if (key === 'group_name' && !obj[key]) {
      return 'N/A';
    }
    return obj[key];
  }
  return undefined;
}

// Simplified parsing: extract only ISO timestamp and numeric user id.
export function parseAcknowledgementList(entry: string) {
  if (!entry) return { time: '', userId: '' };

  const isoRe = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?/;
  const iso = entry.match(isoRe)?.[0];

  let time = '';
  if (iso) {
    const dt = new Date(iso);
    if (!Number.isNaN(dt.getTime())) time = dt.toLocaleString();
  }
  // Remove ISO timestamp and common tokens before extracting numeric id to avoid false matches.
  let rem = entry
    .replace(iso ?? '', '')
    .replace(/\bupdated\b/gi, '')
    .trim();
  rem = rem.replace(/[:()\[\]\-]/g, ' ').replace(/\s+/g, ' ');

  const idMatch =
    rem.match(/(?:user[_\-\s]?id|uid|user id|user)?[:=\s]*\(?\b(\d{2,})\b\)?/i) ||
    rem.match(/\b(\d{3,})\b/);
  const userId = idMatch?.[1] ?? '';

  return { time, userId };
}

// Alarm Listener helpers

/**
 * Returns true if the alarm is expired based on its expireTime.
 * @param alarm - Alarm object with optional info.expireTime
 * @returns {boolean} True if expired, false otherwise
 */
export function isAlarmExpired(alarm: any): boolean {
  const expireTime = alarm?.info?.expireTime ?? null;
  if (expireTime === null) return false;
  const expireMs = new Date(expireTime).getTime();
  return Number.isFinite(expireMs) && Date.now() > expireMs;
}

/**
 * Returns true if the alarm is active based on its state.
 * @param alarm - Alarm object with optional state
 * @returns {boolean} True if active, false otherwise
 */
export function isAlarmActive(alarm: any): boolean {
  const state = alarm?.state;
  if (state === undefined || state === null || state === '') return true;
  if (typeof state === 'string') {
    const normalized = state.trim().toLowerCase();
    if (['inactive', 'resolved', 'cleared', '0', 'false'].includes(normalized)) return false;
    if (['active', '1', 'true'].includes(normalized)) return true;
  }
  const parsedState = Number(state);
  if (Number.isFinite(parsedState)) return parsedState !== 0;
  return Boolean(state);
}

/**
 * Returns a string for alarm announcement text based on config fields.
 * @param alarm - Alarm object
 * @param config - Config object with announcementFields (optional)
 * @returns {string} Announcement text
 */
export function toAnnouncementText(
  alarm: any,
  config?: { announcementFields?: { alertType?: boolean } },
): string {
  const fields = config?.announcementFields || { alertType: true };
  const type = fields.alertType ? String(alarm?.info?.type ?? '').trim() : '';

  let source = '';
  const rawSource = alarm?.source;
  if (typeof rawSource === 'string') {
    source = rawSource.trim();
  } else if (rawSource && typeof rawSource === 'object') {
    const title = String(rawSource.title ?? '').trim() || 'Alarm';
    const details = String(rawSource.details ?? '').trim();
    source = details ? `${title} - ${details}` : title;
  }

  if (type && source) return `${type} via ${source}`;
  if (type) return type;
  return 'Alarm';
}

/**
 * Returns a unique announcement key for an alarm, prioritizing known identifiers.
 * @param alarm - Alarm object
 * @returns {string} Announcement key
 */
export function getAlarmAnnouncementKey(alarm: any): string {
  const id = getAlarmIdentifier(alarm);
  if (id && id !== 'ID_undefined' && id !== 'ID_null') return id;

  const source = String(alarm?.source ?? 'unknown').trim();
  const device = String(alarm?.device ?? '').trim();
  const headline = String(alarm?.headline ?? 'alarm').trim();
  const alertType = String(alarm?.info?.type ?? '').trim();
  const message = String(alarm?.message ?? '').trim();

  return [source, device, headline, alertType, message].join('|');
}

/**
 * Calculates the bounding box for a set of coordinates.
 * @param {[number, number][]} coords
 * @returns {[[number, number], [number, number]] | null} Bounding box coordinates
 */
export function getBoundsFromCoords(
  coords: [number, number][],
): [[number, number], [number, number]] | null {
  if (!Array.isArray(coords) || coords.length === 0) return null;

  const lats = coords.map((c) => c[0]);
  const lngs = coords.map((c) => c[1]);

  return [
    [Math.min(...lats), Math.min(...lngs)],
    [Math.max(...lats), Math.max(...lngs)],
  ];
}
